import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Autocomplete, TextField } from "@mui/material";
import { IoClose } from "react-icons/io5";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { contextProvider } from "../../../setupPage/SetupPagesContextApi/SetupPagesContextApi";
import GetLocalStorageUserId from "../../../sharedComponents/GetLocalStorageuserId";
import { formatCurrency } from "../../../../utils/formatCurrency";
import {
  adminEditBooking,
  fetchParcelCostDetails,
  fetchParcelDetailsByParcelNo,
  fetchParcelProductsForEdit,
  previewTransitionCost,
} from "../../../../api/adminEditBookingApi";
import {
  applyWeightBandToProduct,
  buildEditProductsFromParcel,
  describeAdjustment,
  enrichProductsWithLookups,
  findWeightBandByKg,
  findWeightRowForProduct,
  formatAdjustmentMessage,
  formatWeightBandLabel,
  previousPayableFromParcel,
  sumPreviewTotal,
  toPreviewRequest,
  toSaveProduct,
  weightBandCost,
  weightChargeForKg,
  weightsForRoute,
} from "../../../../utils/adminEditBooking";
import {
  saveOriginalMeasurementSnapshot,
  saveRemeasurementMeta,
  loadRemeasurementMeta,
} from "../../../../utils/invoiceManagerSlip";

const emptyLookups = {
  routes: [],
  cargoTypes: [],
  itemCategory: [],
  itemType: [],
  weight: [],
  dimensions: [],
};

function getApiBase() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchLookup(url) {
  try {
    const { data } = await axios.get(`${getApiBase()}${url}`, {
      headers: { accept: "*/*", ...getAuthHeaders() },
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function pickFirst(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

const AdminEditBookingModal = ({ open, parcel, onClose, onSaved }) => {
  const ctx = useContext(contextProvider) || emptyLookups;

  const [lookups, setLookups] = useState({
    routes: ctx.routes || [],
    cargoTypes: ctx.cargoTypes || [],
    itemCategories: ctx.itemCategory || [],
    itemTypes: ctx.itemType || [],
    weights: ctx.weight || [],
    dimensions: ctx.dimensions || [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [products, setProducts] = useState([]);
  const [isPickup, setIsPickup] = useState(false);
  const [reason, setReason] = useState("");
  const [previousPayable, setPreviousPayable] = useState(0);
  const [previewTotal, setPreviewTotal] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("GBP");

  const parcelId = parcel?.parcelNo;

  const routes = lookups.routes || [];
  const cargoTypes = lookups.cargoTypes || [];
  const itemCategories = lookups.itemCategories || [];
  const itemTypes = lookups.itemTypes || [];

  // Load master data (context may still be null on first open)
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const loadLookups = async () => {
      const [routesData, cargoData, categories, types, weights, dimensions] =
        await Promise.all([
          ctx.routes?.length ? Promise.resolve(ctx.routes) : fetchLookup("Route"),
          ctx.cargoTypes?.length
            ? Promise.resolve(ctx.cargoTypes)
            : fetchLookup("CargoType"),
          ctx.itemCategory?.length
            ? Promise.resolve(ctx.itemCategory)
            : fetchLookup("ItemCategory"),
          ctx.itemType?.length
            ? Promise.resolve(ctx.itemType)
            : fetchLookup("ItemType"),
          ctx.weight?.length ? Promise.resolve(ctx.weight) : fetchLookup("Weight"),
          ctx.dimensions?.length
            ? Promise.resolve(ctx.dimensions)
            : fetchLookup("Dimension"),
        ]);

      if (cancelled) return;
      setLookups({
        routes: routesData,
        cargoTypes: cargoData,
        itemCategories: categories,
        itemTypes: types,
        weights,
        dimensions,
      });
    };

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [open, ctx.routes, ctx.cargoTypes, ctx.itemCategory, ctx.itemType, ctx.weight, ctx.dimensions]);

  useEffect(() => {
    if (!open || !parcel) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setPreviewTotal(null);
      setPreviewRows([]);
      setReason("");
      try {
        let detailPayload = null;
        let productLines = [];
        let costData = {};
        try {
          detailPayload = await fetchParcelDetailsByParcelNo(parcel.parcelNo);
        } catch {
          detailPayload = null;
        }
        try {
          productLines = await fetchParcelProductsForEdit(parcel.parcelNo);
        } catch {
          productLines = [];
        }
        try {
          costData = await fetchParcelCostDetails(parcel.parcelNo);
        } catch {
          costData = {};
        }
        if (cancelled) return;

        const mapped = buildEditProductsFromParcel(
          parcel,
          detailPayload,
          productLines,
        );
        // Capture customer booking measurements once (before hub remesaure overwrites).
        const snapKey = `parcelRemeasureOriginal_${parcel.parcelNo}`;
        if (!sessionStorage.getItem(snapKey)) {
          saveOriginalMeasurementSnapshot(parcel.parcelNo, mapped);
        }

        const enriched = enrichProductsWithLookups(mapped, lookups).map(
          (p, idx) => {
            // User-provided dims already on par_ParcelDetails (ParcelLength/Width/Height/Weight)
            const apiRow =
              (Array.isArray(productLines) && productLines[idx]) ||
              (Array.isArray(productLines) && productLines[0]) ||
              {};
            const userLength = pickFirst(
              apiRow.customerParcelLength,
              apiRow.CustomerParcelLength,
              apiRow.parcelLength,
              apiRow.ParcelLength,
              p.customerParcelLength,
              p.parcelLength,
            );
            const userWidth = pickFirst(
              apiRow.customerParcelWidth,
              apiRow.CustomerParcelWidth,
              apiRow.parcelWidth,
              apiRow.ParcelWidth,
              p.customerParcelWidth,
              p.parcelWidth,
            );
            const userHeight = pickFirst(
              apiRow.customerParcelHeight,
              apiRow.CustomerParcelHeight,
              apiRow.parcelHeight,
              apiRow.ParcelHeight,
              p.customerParcelHeight,
              p.parcelHeight,
            );
            // Customer weight must come from CustomerParcelWeight — not remesaure ParcelWeight
            const userWeight = pickFirst(
              apiRow.customerParcelWeight,
              apiRow.CustomerParcelWeight,
              p.customerParcelWeight,
            );
            const hubWeight = pickFirst(
              apiRow.parcelWeight,
              apiRow.ParcelWeight,
              p.parcelWeight,
            );

            const withCustomer = {
              ...p,
              customerParcelLength:
                userLength !== "" && userLength != null ? String(userLength) : "",
              customerParcelWidth:
                userWidth !== "" && userWidth != null ? String(userWidth) : "",
              customerParcelHeight:
                userHeight !== "" && userHeight != null
                  ? String(userHeight)
                  : "",
              customerParcelWeight:
                userWeight !== "" && userWeight != null
                  ? String(userWeight)
                  : hubWeight !== "" && hubWeight != null
                    ? String(hubWeight)
                    : "",
              parcelWeight:
                hubWeight !== "" && hubWeight != null
                  ? String(hubWeight)
                  : p.parcelWeight || "",
            };
            const weightRow = findWeightRowForProduct(
              withCustomer,
              lookups.weights || [],
            );
            return weightRow
              ? applyWeightBandToProduct(
                  withCustomer,
                  weightRow,
                  lookups.dimensions || [],
                )
              : withCustomer;
          },
        );
        setProducts(enriched);
        setIsPickup(
          Boolean(
            productLines?.[0]?.isPickup ??
              productLines?.[0]?.isPickUp ??
              detailPayload?.isPickup ??
              detailPayload?.isPickUp ??
              parcel.isPickup ??
              parcel.isPickUp ??
              false,
          ),
        );
        const prev = previousPayableFromParcel(parcel, costData);
        setPreviousPayable(prev);
        setCurrencySymbol(
          parcel.details?.[0]?.symbol ||
            costData.symbol ||
            costData.currencyType ||
            "GBP",
        );
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load booking for edit.",
        );
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // Re-run when lookups finish so names can resolve to ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parcel, lookups.routes, lookups.weights, lookups.dimensions]);

  const filteredItemTypes = useMemo(() => {
    if (!itemTypes?.length) return [];
    return itemTypes;
  }, [itemTypes]);

  const updateProduct = (index, patch) => {
    setProducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
    setPreviewTotal(null);
  };

  const weightRecalcTimer = useRef(null);

  const recalculateWithProducts = useCallback(
    async (nextProducts, { silent = false } = {}) => {
      if (!nextProducts?.length) return false;

      for (const p of nextProducts) {
        if (!p.routingTypeId || !p.itemCategoryId || !p.itemTypeId) {
          if (!silent) {
            toast.error("Each product needs route, category, and item type.");
          }
          return false;
        }
        if (!(Number(p.parcelWeight) > 0)) {
          if (!silent) {
            toast.error("Weight (kg) must be greater than 0.");
          }
          return false;
        }
      }

      setRecalculating(true);
      try {
        const requests = nextProducts.map((p) => toPreviewRequest(p, isPickup));
        const rows = await previewTransitionCost(requests);
        setPreviewRows(rows);
        const total = sumPreviewTotal(rows);
        setPreviewTotal(Number(total.toFixed(2)));
        if (!silent) toast.success("Price recalculated.");
        return true;
      } catch (err) {
        setPreviewTotal(null);
        setPreviewRows([]);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to recalculate price.",
        );
        return false;
      } finally {
        setRecalculating(false);
      }
    },
    [isPickup],
  );

  const scheduleRecalculate = useCallback(
    (nextProducts) => {
      if (weightRecalcTimer.current) {
        clearTimeout(weightRecalcTimer.current);
      }
      weightRecalcTimer.current = setTimeout(() => {
        recalculateWithProducts(nextProducts, { silent: true });
      }, 350);
    },
    [recalculateWithProducts],
  );

  useEffect(() => {
    return () => {
      if (weightRecalcTimer.current) clearTimeout(weightRecalcTimer.current);
    };
  }, []);

  const handleWeightBandChange = async (index, weightId) => {
    const product = products[index];
    if (!product) return;
    if (parcelId && !sessionStorage.getItem(`parcelRemeasureOriginal_${parcelId}`)) {
      saveOriginalMeasurementSnapshot(parcelId, products);
    }
    const options = weightsForRoute(
      lookups.weights || [],
      product.routingTypeId,
    );
    const weightRow = options.find((w) => String(w.id) === String(weightId));
    let nextProducts;
    if (!weightRow) {
      nextProducts = products.map((p, i) =>
        i === index ? { ...p, weightId: "" } : p,
      );
      setProducts(nextProducts);
      setPreviewTotal(null);
      setPreviewRows([]);
      return;
    }
    const next = applyWeightBandToProduct(
      product,
      weightRow,
      lookups.dimensions || [],
    );
    nextProducts = products.map((p, i) => (i === index ? next : p));
    setProducts(nextProducts);
    await recalculateWithProducts(nextProducts, { silent: true });
  };

  const handleWeightKgChange = (index, value) => {
    const product = products[index];
    if (!product) return;
    if (parcelId && !sessionStorage.getItem(`parcelRemeasureOriginal_${parcelId}`)) {
      saveOriginalMeasurementSnapshot(parcelId, products);
    }

    let next = { ...product, parcelWeight: value };
    const band = findWeightBandByKg(
      value,
      lookups.weights || [],
      product.routingTypeId,
    );

    if (band) {
      next = applyWeightBandToProduct(next, band, lookups.dimensions || [], {
        preserveParcelWeight: true,
      });
    } else if (value === "" || !(Number(value) > 0)) {
      next = { ...next, weightId: next.weightId || "" };
    }

    const nextProducts = products.map((p, i) => (i === index ? next : p));
    setProducts(nextProducts);
    setPreviewTotal(null);

    if (Number(value) > 0 && next.routingTypeId && next.itemCategoryId && next.itemTypeId) {
      scheduleRecalculate(nextProducts);
    } else {
      setPreviewRows([]);
    }
  };

  const adjustment = useMemo(() => {
    if (previewTotal == null) return null;
    return describeAdjustment(previousPayable, previewTotal);
  }, [previewTotal, previousPayable]);

  const handleRecalculate = async () => {
    await recalculateWithProducts(products, { silent: false });
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required for audit.");
      return;
    }
    if (previewTotal == null) {
      toast.error("Please recalculate price before saving.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm remesaure / edit",
      html: `<p class="text-left">${formatAdjustmentMessage(
        previousPayable,
        previewTotal,
      )}</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Save booking",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ea580c",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      // Persist customer (pre-remeasure) vs hub measurements for invoice slips
      try {
        const existingMeta = loadRemeasurementMeta(parcelId);
        const originalSnap =
          existingMeta?.customerInput ||
          (products[0]
            ? {
                length:
                  products[0].customerParcelLength ||
                  products[0].parcelLength ||
                  "",
                width:
                  products[0].customerParcelWidth ||
                  products[0].parcelWidth ||
                  "",
                height:
                  products[0].customerParcelHeight ||
                  products[0].parcelHeight ||
                  "",
                weight:
                  products[0].customerParcelWeight ||
                  products[0].parcelWeight ||
                  "",
              }
            : null) ||
          (() => {
            try {
              const raw =
                localStorage.getItem(`parcelRemeasureOriginal_${parcelId}`) ||
                sessionStorage.getItem(`parcelRemeasureOriginal_${parcelId}`);
              if (!raw) return null;
              const parsed = JSON.parse(raw);
              const first = Array.isArray(parsed) ? parsed[0] : parsed;
              if (!first) return null;
              return {
                length: first.parcelLength || first.itemLength || "",
                width: first.parcelWidth || first.itemWidth || "",
                height: first.parcelHeight || first.itemHeight || "",
                weight: first.parcelWeight || first.itemWeight || "",
              };
            } catch {
              return null;
            }
          })();
        const hub = products[0]
          ? {
              length: products[0].parcelLength || "",
              width: products[0].parcelWidth || "",
              height: products[0].parcelHeight || "",
              weight: products[0].parcelWeight || "",
            }
          : null;
        if (originalSnap || hub) {
          saveRemeasurementMeta(parcelId, {
            customerInput: originalSnap,
            hubMeasurement: hub,
          });
        }
      } catch {
        /* non-blocking */
      }

      const payload = {
        parcelId: Number(parcelId),
        modifierId: String(GetLocalStorageUserId() || ""),
        reason: reason.trim(),
        isPickup: Boolean(isPickup),
        products: products.map(toSaveProduct),
      };
      const result = await adminEditBooking(payload);

      const invoice =
        result?.invoiceNumber ||
        result?.payments?.find((p) => p.paymentInvoiceNo)?.paymentInvoiceNo;

      toast.success(
        invoice
          ? `Booking updated. Invoice: ${invoice}`
          : "Booking updated successfully.",
      );
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.title ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save booking edit.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="w-full max-w-5xl my-6 rounded-xl bg-white shadow-2xl border border-stone-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-stone-50 rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Edit booking / Remesaure
            </h2>
            <p className="text-sm text-gray-600">
              Parcel #{parcelId} — server recalculates final amounts
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white"
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <p className="text-center py-10 text-gray-600">Loading booking…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Payable
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-1">
                    {previewTotal == null
                      ? "—"
                      : formatCurrency(previewTotal, currencySymbol)}
                  </p>
                </div>
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Paid
                  </p>
                  <p className="text-xl font-bold text-emerald-700 mt-1">
                    {formatCurrency(previousPayable, currencySymbol)}
                  </p>
                </div>
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    New balance
                  </p>
                  <p className="text-xl font-bold text-orange-600 mt-1">
                    {previewTotal == null
                      ? "—"
                      : formatCurrency(
                          Number((previewTotal - previousPayable).toFixed(2)),
                          currencySymbol,
                        )}
                  </p>
                </div>
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Due
                  </p>
                  <p className="text-xl font-bold mt-1">
                    {adjustment == null ? (
                      "—"
                    ) : adjustment.type === "Credit" ? (
                      <span className="text-emerald-700">
                        {formatCurrency(0, currencySymbol)}
                        <span className="block text-xs font-semibold mt-0.5">
                          Credit {formatCurrency(adjustment.amount, currencySymbol)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-amber-700">
                        {formatCurrency(adjustment.amount, currencySymbol)}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={isPickup}
                  onChange={(e) => {
                    setIsPickup(e.target.checked);
                    setPreviewTotal(null);
                  }}
                />
                Pickup required
              </label>

              <div className="space-y-4">
                {products.map((product, index) => (
                  <div
                    key={product.parcelDetailId || index}
                    className="rounded-lg border border-stone-200 p-4 bg-white"
                  >
                    <p className="font-semibold text-gray-900 mb-1">
                      Product line {index + 1}
                      {product.parcelDetailId
                        ? ` · detail #${product.parcelDetailId}`
                        : ""}
                    </p>
                    {(product.weightDescription ||
                      product.dimensionName ||
                      product.itemTypeName) && (
                      <p className="text-xs text-gray-500 mb-3">
                        From booking:{" "}
                        {[
                          product.routeName,
                          product.weightDescription,
                          product.dimensionName,
                          product.itemTypeName,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        . Change weight band only — dimensions follow the selected band.
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Route
                        </label>
                        <select
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={
                            product.routingTypeId != null &&
                            product.routingTypeId !== ""
                              ? String(product.routingTypeId)
                              : ""
                          }
                          onChange={(e) =>
                            updateProduct(index, {
                              routingTypeId: e.target.value,
                              weightId: "",
                            })
                          }
                        >
                          <option value="">Select route</option>
                          {routes.map((r) => (
                            <option key={r.id} value={String(r.id)}>
                              {r.routeName || r.name || r.id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Item category
                        </label>
                        <select
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={
                            product.itemCategoryId != null &&
                            product.itemCategoryId !== ""
                              ? String(product.itemCategoryId)
                              : ""
                          }
                          onChange={(e) =>
                            updateProduct(index, {
                              itemCategoryId: e.target.value,
                            })
                          }
                        >
                          <option value="">Select category</option>
                          {itemCategories.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {c.name || c.itemCategoryName || c.id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Item type
                        </label>
                        <select
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={
                            product.itemTypeId != null &&
                            product.itemTypeId !== ""
                              ? String(product.itemTypeId)
                              : ""
                          }
                          onChange={(e) =>
                            updateProduct(index, {
                              itemTypeId: e.target.value,
                            })
                          }
                        >
                          <option value="">Select type</option>
                          {filteredItemTypes.map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              {t.name || t.itemTypeName || t.id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Cargo type
                        </label>
                        <select
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={
                            product.cargoTypeId != null &&
                            product.cargoTypeId !== ""
                              ? String(product.cargoTypeId)
                              : ""
                          }
                          onChange={(e) =>
                            updateProduct(index, {
                              cargoTypeId: e.target.value,
                            })
                          }
                        >
                          <option value="">Select cargo</option>
                          {cargoTypes.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {c.cargoTypeName || c.name || c.id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-gray-500">
                            Weight band
                          </label>
                          {(() => {
                            const selected =
                              findWeightRowForProduct(
                                product,
                                weightsForRoute(
                                  lookups.weights || [],
                                  product.routingTypeId,
                                ),
                              ) ||
                              (lookups.weights || []).find(
                                (w) =>
                                  String(w.id) === String(product.weightId),
                              );
                            const unit = weightBandCost(selected);
                            const charge = weightChargeForKg(
                              product.parcelWeight,
                              unit,
                            );
                            if (unit == null) return null;
                            return (
                              <span className="inline-flex items-center rounded-md bg-stone-900 px-2 py-0.5 text-xs font-bold text-white">
                                {charge != null
                                  ? `${product.parcelWeight || 0}kg × ${formatCurrency(unit, currencySymbol)}/kg = ${formatCurrency(charge, currencySymbol)}`
                                  : `${formatCurrency(unit, currencySymbol)}/kg`}
                              </span>
                            );
                          })()}
                        </div>
                        {(() => {
                          const routeWeights = weightsForRoute(
                            lookups.weights || [],
                            product.routingTypeId,
                          );
                          const selected =
                            routeWeights.find(
                              (w) => String(w.id) === String(product.weightId),
                            ) ||
                            findWeightRowForProduct(product, routeWeights) ||
                            null;

                          return (
                            <Autocomplete
                              className="mt-1"
                              options={routeWeights}
                              value={selected}
                              onChange={(_event, option) =>
                                handleWeightBandChange(
                                  index,
                                  option?.id != null ? String(option.id) : "",
                                )
                              }
                              getOptionLabel={(option) =>
                                formatWeightBandLabel(
                                  option,
                                  lookups.dimensions || [],
                                  currencySymbol,
                                )
                              }
                              isOptionEqualToValue={(option, value) =>
                                String(option?.id) === String(value?.id)
                              }
                              filterOptions={(options, state) => {
                                const q = String(state.inputValue || "")
                                  .trim()
                                  .toLowerCase();
                                if (!q) return options;
                                return options.filter((w) => {
                                  const label = formatWeightBandLabel(
                                    w,
                                    lookups.dimensions || [],
                                    currencySymbol,
                                  ).toLowerCase();
                                  const desc = String(
                                    w.weightDescription || "",
                                  ).toLowerCase();
                                  const min = String(w.minimumWeight ?? "");
                                  const max = String(w.maximumWeight ?? "");
                                  return (
                                    label.includes(q) ||
                                    desc.includes(q) ||
                                    min.includes(q) ||
                                    max.includes(q) ||
                                    `${min}-${max}`.includes(q)
                                  );
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Search weight band…"
                                  size="small"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0.5rem",
                                      fontSize: "0.875rem",
                                      fontWeight: 500,
                                      backgroundColor: "#fff",
                                      "& fieldset": {
                                        borderWidth: 2,
                                        borderColor: "#d6d3d1",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "#d6d3d1",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#d6d3d1",
                                      },
                                    },
                                  }}
                                />
                              )}
                              ListboxProps={{
                                style: { maxHeight: 280 },
                              }}
                            />
                          );
                        })()}
                        {(() => {
                          const selected =
                            findWeightRowForProduct(
                              product,
                              weightsForRoute(
                                lookups.weights || [],
                                product.routingTypeId,
                              ),
                            ) ||
                            (lookups.weights || []).find(
                              (w) =>
                                String(w.id) === String(product.weightId),
                            );
                          const bandPrice = weightBandCost(selected);
                          return (
                            <p className="text-[11px] text-gray-500 mt-1">
                              Weight charge = kg × band rate
                              {bandPrice != null
                                ? ` (${formatCurrency(bandPrice, currencySymbol)}/kg)`
                                : ""}
                              . Type to search bands.
                            </p>
                          );
                        })()}
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Weight (kg)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="mt-1 w-full border-2 border-stone-300 rounded-lg px-3 py-2 text-sm font-medium bg-white"
                          value={product.parcelWeight}
                          onChange={(e) =>
                            handleWeightKgChange(index, e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={product.productQty}
                          onChange={(e) =>
                            updateProduct(index, {
                              productQty: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <label className="text-xs font-semibold text-gray-500">
                          Customer given dimensions
                        </label>
                        <p className="text-[11px] text-stone-500 mt-0.5 mb-1">
                          From booking (ParcelLength / Width / Height / Weight)
                        </p>
                        <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Length
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.customerParcelLength || "—"}
                            </p>
                          </div>
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Width
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.customerParcelWidth || "—"}
                            </p>
                          </div>
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Height
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.customerParcelHeight || "—"}
                            </p>
                          </div>
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Weight (kg)
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.customerParcelWeight || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <label className="text-xs font-semibold text-gray-500">
                          Dimensions (from weight band — not editable)
                        </label>
                        <div className="mt-1 grid grid-cols-3 gap-2">
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Length
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.parcelLength || "—"}
                            </p>
                          </div>
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Width
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.parcelWidth || "—"}
                            </p>
                          </div>
                          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                              Height
                            </p>
                            <p className="text-lg font-bold text-stone-800">
                              {product.parcelHeight || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm font-medium pb-2">
                          <input
                            type="checkbox"
                            checked={!!product.isExtraPackaging}
                            onChange={(e) =>
                              updateProduct(index, {
                                isExtraPackaging: e.target.checked,
                              })
                            }
                          />
                          Extra packaging
                        </label>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-gray-500">
                          Item description
                        </label>
                        <input
                          type="text"
                          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          value={product.uniqItemDescription || ""}
                          onChange={(e) =>
                            updateProduct(index, {
                              uniqItemDescription: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Reason (required)
                </label>
                <textarea
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  placeholder="e.g. Hub remesaure 5kg → 7kg"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              {previewRows.length > 0 && (
                <div className="rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-right">Weight cost</th>
                        <th className="px-3 py-2 text-right">Dim cost</th>
                        <th className="px-3 py-2 text-right">VAT</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{i + 1}</td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(row.weightCost || 0, currencySymbol)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(
                              row.dimensionCost || 0,
                              currencySymbol,
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(
                              row.vatPercentageAmount || 0,
                              currencySymbol,
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {formatCurrency(
                              row.totalCostWithVAT ??
                                row.totalCost ??
                                row.totalCostWithoutVAT ??
                                0,
                              currencySymbol,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-stone-200 bg-stone-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || recalculating || saving}
            onClick={handleRecalculate}
            className="px-4 py-2 rounded-lg bg-stone-900 text-white font-semibold disabled:opacity-60"
          >
            {recalculating ? "Recalculating…" : "Recalculate"}
          </button>
          <button
            type="button"
            disabled={loading || saving || previewTotal == null}
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-orange-500 text-white font-semibold disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save edit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEditBookingModal;
